import React, { useState, useRef, useEffect } from 'react';
import { useChatbot } from '../../Context/ChatbotContext';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Stack,
  Fab,
  Drawer,
  AppBar,
  Toolbar,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Person as UserIcon,
} from '@mui/icons-material';
import ChatbotIcon from '../Icons/ChatbotIcon';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import ReactMarkdown from 'react-markdown';
import { useTheme } from '@mui/material/styles';
import AttachmentsDisplay from './AttachmentsDisplay';
import PreviewAttachmentDialog from '../PreviewAttachmentDialog';
import { UserDetailsContext } from '../../Context/userDetailsContext';
import { ConfigLinksDetailsContext } from '../../Context/cofingLinksDetailsContext';
import { getAvatarInitials, getAvatarColor } from '../../utils/avatarUtils';

const Chatbot = ({ open, onClose, onOpen }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';
  const isArabic = i18n.language === 'ar';
  const { userDetails } = React.useContext(UserDetailsContext);
  const { configLinks } = React.useContext(ConfigLinksDetailsContext);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAttachmentPreview, setShowAttachmentPreview] = useState(false);
  const [attachmentToPreview, setAttachmentToPreview] = useState(null);
  const messagesEndRef = useRef(null);

  const municipalityName = isArabic
    ? (configLinks?.municipalityNameAr || "البلدية")
    : (configLinks?.municipalityNameEn || "Municipality");

  const assistantName = isArabic
    ? `مساعد ${municipalityName} الذكي`
    : `${municipalityName} Smart Assistant`;

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Also scroll to bottom when drawer opens
  useEffect(() => {
    if (open) {
      // small delay to ensure Drawer content is rendered
      const id = setTimeout(scrollToBottom, 150);
      return () => clearTimeout(id);
    }
  }, [open]);

  // Initialize with welcome message
  useEffect(() => {
    // Only show welcome message if we have the actual municipality name (not the default)
    const hasRealMunicipalityName = configLinks?.municipalityNameAr || configLinks?.municipalityNameEn;

    if (messages.length === 0 && hasRealMunicipalityName) {
      const welcomeMessage = isArabic
        ? `مرحباً! أنا ${assistantName}. كيف يمكنني مساعدتك اليوم؟`
        : `Hello! I am ${assistantName}. How can I help you today?`;

      setMessages([
        {
          id: 1,
          text: welcomeMessage,
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    }
  }, [isArabic, assistantName, configLinks, messages.length]);

  const handlePreviewAttachment = (attachment) => {
    setAttachmentToPreview(attachment);
    setShowAttachmentPreview(true);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post('/Chatbot/chat', {
        message: inputMessage,
        history: messages
          .filter((m) => m.sender !== 'bot' || m.id !== 1)
          .slice(-6) // Limit history to last 6 messages (3 from user, 3 from bot)
          .map((m) => ({
            role: m.sender === 'user' ? 'user' : 'assistant',
            content: m.text,
          })),
      });

      const payload = response?.data;
      const botMessage = {
        id: messages.length + 2,
        text:
          payload?.dataItem?.response ||
          payload?.dataItem?.Response ||
          payload?.message ||
          (isArabic
            ? 'عذراً، لم يتم تلقي رد. يرجى المحاولة مرة أخرى.'
            : 'No response received. Please try again.'),
        sender: 'bot',
        timestamp: new Date(),
        attachments: payload?.dataItem?.attachments || payload?.attachments,
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);

      // Check if error is due to authentication requirement
      const errorData = error?.response?.data || error;
      let errorText;

      if (errorData?.message && errorData.message.includes('login')) {
        errorText = isArabic
          ? 'هذه المعلومات تتطلب تسجيل الدخول. يرجى تسجيل الدخول أولاً.'
          : 'This information requires login. Please log in first.';
      } else if (errorData?.message) {
        errorText = errorData.message;
      } else {
        errorText = isArabic
          ? 'عذراً، حدث خطأ في الاتصال. يرجى المحاولة مرة أخرى.'
          : 'Sorry, a connection error occurred. Please try again.';
      }

      const errorMessage = {
        id: messages.length + 2,
        text: errorText,
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickQuestions = isArabic
    ? [
        'ما هي الخدمات المتاحة؟',
        'كيف يمكنني التواصل مع البلدية؟',
        'ما هي طلباتي؟',
        'كيف أقدم طلب؟',
      ]
    : [
        'What services are available?',
        'How can I contact the municipality?',
        'What are my requests?',
        'How do I submit a request?',
      ];

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
  };

  return (
    <Drawer
      anchor={'left'}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          position: 'absolute',
          width: { xs: '100%', md: 420 },
          height: { xs: '70vh', md: '65vh' },
          boxShadow: 3,
          top: 'auto',
          bottom: { xs: 0, md: 24 },
          left: { xs: 0, md: 90 },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ height: { xs: '70vh', md: '65vh' }, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <AppBar position="static" sx={{ backgroundColor: 'primary.main' }}>
          <Toolbar>
             <IconButton color="inherit" onClick={onClose}>
              <CloseIcon />
            </IconButton>
      
            <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'right',mx: 2 }}>
              {assistantName}
            </Typography>
               <Avatar sx={{ bgcolor: 'transparent', fontSize: 24 , borderRadius:10, border: '1.5px solid white' ,padding:"4px" }}>
              <ChatbotIcon />
            </Avatar>
          </Toolbar>
        </AppBar>

        {/* Quick Questions */}
        {messages.length <= 1 && (
          <Box sx={{ p: 2, backgroundColor: '#f5f5f5', direction: isArabic ? 'rtl' : 'ltr' }}>
            <Typography
              variant="caption"
              sx={{
                mb: 1,
                display: 'block',
                fontWeight: 'bold',
                textAlign: isArabic ? 'right' : 'left',
                direction: isArabic ? 'rtl' : 'ltr'
              }}
            >
              {isArabic ? 'أسئلة شائعة:' : 'Common Questions:'}
            </Typography>
            <Stack
              direction={isArabic ? 'row-reverse' : 'row'}
              spacing={1}
              flexWrap="wrap"
              gap={1}
              sx={{
                justifyContent: isArabic ? 'flex-end' : 'flex-start',
                direction: isArabic ? 'rtl' : 'ltr'
              }}
            >
              {quickQuestions.map((question, index) => (
                <Chip
                  key={index}
                  label={question}
                  onClick={() => handleQuickQuestion(question)}
                  size="small"
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Messages Area */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: 2,
            backgroundColor: '#fafafa',
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.sender === 'user' ? (isArabic ? 'flex-start' : 'flex-end') : (isArabic ? 'flex-end' : 'flex-start'),
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: message.sender === 'user' ? (isArabic ? 'row' : 'row-reverse') : (isArabic ? 'row-reverse' : 'row'),
                  alignItems: 'flex-start',
                  maxWidth: '80%',
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: message.sender === 'user' 
                      ? (userDetails?.isAuthenticated 
                          ? getAvatarColor(`${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`)
                          : 'primary.main'
                        )
                      : 'secondary.main',
                    width: 34,
                    height: 34,
                    mx: 1,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                  }}
                >
                  {message.sender === 'user'
                    ? (userDetails?.isAuthenticated
                        ? getAvatarInitials(`${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`)
                        : <UserIcon fontSize="small" />
                      )
                    : <ChatbotIcon  />
                  }
                </Avatar>
                <Paper
                  sx={{
                    py: 0.0,
                    px: 1.5,
                    backgroundColor: message.sender === 'user' ? 'primary.light' : 'white',
                    color: message.sender === 'user' ? 'white' : 'text.primary',
                    borderRadius: 4,
                    direction: isArabic ? 'rtl' : 'ltr',
                    textAlign: isArabic ? 'right' : 'left',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                  }}
                >
                  <Box
                    sx={{
                      '& p': { margin: '0.5em 0', direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left', wordWrap: 'break-word' },
                      '& ul, & ol': { paddingInlineStart: '1.5em', margin: '0.5em 0', direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left' },
                      '& li': { marginBottom: '0.25em', direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left', wordWrap: 'break-word' },
                      '& h1, & h2, & h3': { margin: '0.5em 0', fontWeight: 'bold', direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left', wordWrap: 'break-word' },
                      '& strong': { fontWeight: 'bold' },
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      direction: isArabic ? 'rtl' : 'ltr',
                      textAlign: isArabic ? 'right' : 'left',
                      fontFamily: isArabic ? '"Noto Sans Arabic", "Segoe UI", Roboto, sans-serif' : '"Segoe UI", Roboto, sans-serif',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  >
                    <ReactMarkdown>
                      {message.text}
                    </ReactMarkdown>
                    {message.attachments && message.attachments.length > 0 && (
                      <AttachmentsDisplay
                        attachments={message.attachments}
                        onPreview={handlePreviewAttachment}
                      />
                    )}
                  </Box>
                </Paper>
              </Box>
            </Box>
          ))}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: isArabic ? 'flex-end' : 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: isArabic ? 'row-reverse' : 'row' }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32, mx: 1 }}>
                  <ChatbotIcon fontSize="small" />
                </Avatar>
                <Paper sx={{ p: 1.5, borderRadius: 4 }}>
                  <Stack direction={isArabic ? 'row-reverse' : 'row'} spacing={1.5} alignItems="center">
                    <CircularProgress size={16} sx={{ color: 'secondary.main' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      {isArabic ? 'يفكر...' : 'Thinking...'}
                    </Typography>
                  </Stack>
                </Paper>
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Paper
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'white',
          }}
        >
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isArabic ? 'اكتب رسالتك هنا...' : 'Type your message here...'}
              FormHelperTextProps={{
                sx: {
                  textAlign: isArabic ? 'right' : 'left',
                  direction: isArabic ? 'rtl' : 'ltr',
                }
              }}
              variant="outlined"
              size="small"
              dir={isArabic ? 'rtl' : 'ltr'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 4,
                },
                '& .MuiInputBase-input': {
                  textAlign: isArabic ? 'right' : 'left',
                  direction: isArabic ? 'rtl' : 'ltr',
                },
              }}
            />
            <IconButton color="primary" onClick={handleSendMessage} disabled={isLoading}>
              {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Stack>
        </Paper>
        {showAttachmentPreview && (
          <PreviewAttachmentDialog
            open={showAttachmentPreview}
            handleClose={() => setShowAttachmentPreview(false)}
            fileData={attachmentToPreview}
          />
        )}
      </Box>
    </Drawer>
  );
};

// Floating Button Component
export const ChatbotButton = () => {
  const [open, setOpen] = useState(false);
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const { chatbotSettings } = useChatbot();
  const theme = useTheme();

  // Broadcast open state changes so other floating UI can react (e.g., hide when chatbot is open)
  useEffect(() => {
    const event = new CustomEvent('chatbot-open-changed', { detail: { open } });
    window.dispatchEvent(event);
  }, [open]);

  // Don't render the button if chatbot is disabled
  if (!chatbotSettings.enabled) {
    return null;
  }

  return (
    <>
      <Fab
        color="primary"
        aria-label="chatbot"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 22,
          left: 20,
          zIndex: 1000,
          backgroundColor: 'primary.main',
          color: 'white',
          '&:hover': {
            backgroundColor: 'primary.dark',
            transform: 'scale(1.1)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
      >
        <ChatbotIcon sx={{ fontSize: 28 }} />
      </Fab>
      <Chatbot open={open} onClose={() => setOpen(false)} onOpen={() => setOpen(true)} />
    </>
  );
};

export default Chatbot;
